import { attachmentTypeCodes } from 'shared/constants';
import { BaseFileTypeDto } from '@eva/types/dto';

// eslint-disable-next-line import/no-cycle
import {
  getFileTypeCategory,
  getFileTypeCategoryId,
  getFileTypeCode,
  getFileTypeId,
  getFileTypeName,
} from 'containers/FilesTable';

export interface FileTypeOption<P> {
  label: string;
  value: number;
  code: string;
  payload?: P;
}

export interface FileTypeOptionGroup<P> {
  label: string;
  options: FileTypeOption<P>[];
  categoryId: number | null;
}

export const getActiveFileTypes = (fileTypes: BaseFileTypeDto[]) =>
  fileTypes ? fileTypes.filter((cft) => !cft.disabled) : fileTypes;

export const optionsSorter = <P extends BaseFileTypeDto>(option1: FileTypeOption<P>, option2: FileTypeOption<P>) => {
  if (option1.code === attachmentTypeCodes.noType) {
    return -1;
  }
  if (option2.code === attachmentTypeCodes.noType) {
    return 1;
  }
  return (option1.payload?.sortOrder || 0) - (option2.payload?.sortOrder || 0);
};

export const sortOptions = <P extends BaseFileTypeDto>(options: FileTypeOption<P>[]) =>
  options ? [...options].sort(optionsSorter) : options;

export const optionGroupsSorter = <P>(optionGroup1: FileTypeOptionGroup<P>, optionGroup2: FileTypeOptionGroup<P>) => {
  if (optionGroup1.categoryId == null) {
    return -1;
  }
  if (optionGroup2.categoryId == null) {
    return 1;
  }
  return optionGroup1.label.localeCompare(optionGroup2.label);
};

export const sortOptionGroups = <P>(optionGroups: FileTypeOptionGroup<P>[]) =>
  optionGroups ? [...optionGroups].sort(optionGroupsSorter) : optionGroups;

export const buildOptionFromFileType = <FT extends BaseFileTypeDto>(fileType: FT) => ({
  value: getFileTypeId(fileType),
  label: getFileTypeName(fileType),
  code: getFileTypeCode(fileType),
  isDisabled: fileType?.disabled,
  payload: fileType,
});

export const getOptionGroups = <FT extends BaseFileTypeDto>(fileTypes: FT[]) => {
  const cache = fileTypes?.reduce(
    (acc, fileType) => {
      const category = getFileTypeCategory(fileType);
      const categoryId = getFileTypeCategoryId(category) || null;

      acc[categoryId] = acc[categoryId] || {
        label: category?.name || translate('Other'),
        categoryId,
        options: [],
      };

      const option = buildOptionFromFileType(fileType);

      acc[categoryId].options.push(option);

      return acc;
    },
    {} as { [key: number]: FileTypeOptionGroup<FT> },
  );

  return Object.values(cache);
};

export const buildOptionGroups = <FT extends BaseFileTypeDto>(fileTypes: FT[]) => {
  const optionGroups = getOptionGroups(fileTypes);

  return sortOptionGroups(
    optionGroups.map((group) => ({
      ...group,
      options: sortOptions(group.options),
    })),
  );
};
